/**
 * Pure business rules for the Additional Cost Item modal, ported from V1
 * `lib_editrecord.pl` (`sub additionalcost` render + `$saveclick` validation).
 *
 * Kept separate from the component so the gating / validation logic is unit
 * testable without rendering. The component wires these into `disabled` props,
 * option lists, and the save guard.
 */
import type { CostItem } from '../../services/additionalCostItems.service';

/** Canonical Pay Status list (mirrors backend PAYSTATUS_VALUES). */
export const PAYSTATUS_ALL = [
  '', 'PAY', 'BILL BACK', 'BILLED DIRECT', 'CHARGE BACK',
  'DISPUTE', 'NOT RECEIVED', 'REIMBURSE', 'WAIVED',
];

/** Selectable header statuses (mirrors backend STATUS_VALUES). */
export const BASE_STATUS_OPTIONS = ['OPEN', 'HOLD', 'APPROVED', 'MANUAL TRANSFER'];

/** Statuses at/after approval — V1 treats these as "locked identity". */
const APPROVED_LIKE = ['APPROVED', 'MANUAL TRANSFER', 'EXPORTED'];

/** Pay statuses hidden once the header is approved (V1 lib_editrecord.pl:5440). */
const APPROVAL_EXCLUDED_PAYSTATUS = new Set(['NOT RECEIVED', 'DISPUTE', 'WAIVED']);

const filled = (s: string | null | undefined): boolean => !!(s && s.trim());

export const isApproved = (form: Pick<CostItem, 'approveddatetime'>): boolean =>
  filled(form.approveddatetime);

export const isExported = (form: Pick<CostItem, 'masexportdate'>): boolean =>
  filled(form.masexportdate);

/**
 * Identity + amount fields (Vendor, Type, Invoice #/Date, Received Date, PO#,
 * line Qty/Rate, Deleted) lock once the item is approved OR exported.
 * V1 `$global_ro` (lib_editrecord.pl:4738).
 */
export const isIdentityLocked = (
  form: Pick<CostItem, 'approveddatetime' | 'masexportdate'>,
): boolean => isApproved(form) || isExported(form);

/**
 * The Status dropdown stays editable after approval (so a row can be sent back
 * to OPEN/HOLD) and only locks once EXPORTED (V1 lib_editrecord.pl:5286).
 */
export const isStatusFieldLocked = (form: Pick<CostItem, 'masexportdate'>): boolean =>
  isExported(form);

/**
 * A line's Pay Status / GL Code become read-only when that line is WAIVED /
 * NOT RECEIVED and the header is approved-or-later
 * (V1 lib_editrecord.pl:5451, 5467).
 */
export const isLineApprovalLocked = (linePaystatus: string, status: string): boolean =>
  /^(WAIVED|NOT RECEIVED)$/.test(linePaystatus || '') && APPROVED_LIKE.includes(status);

/**
 * Filter the Pay Status options for a line: drop NOT RECEIVED / DISPUTE /
 * WAIVED once the header is approved-or-later, but always keep the line's
 * current value so a legacy row still round-trips (V1 smod_forcevalueinarray).
 */
export function filterPayStatuses(
  status: string,
  all: string[],
  current: string,
): string[] {
  if (!APPROVED_LIKE.includes(status)) return all;
  const filtered = all.filter((s) => !APPROVAL_EXCLUDED_PAYSTATUS.has(s));
  if (current && !filtered.includes(current)) filtered.push(current);
  return filtered;
}

/**
 * Ensure a stored, non-selectable status (e.g. EXPORTED) still renders in the
 * Status dropdown as the current value.
 */
export function statusOptions(base: string[], current: string): string[] {
  if (current && !base.includes(current)) return [...base, current];
  return base;
}

/**
 * Moving a row back to OPEN/HOLD clears the approver stamp and adjust amount
 * (V1 lib_editrecord.pl:5793, 5869, 5874). Returns the fields to overwrite, or
 * null when the target status keeps them.
 */
export function clearApprovalOnDowngrade(
  newStatus: string,
): Pick<CostItem, 'approvedby' | 'approveddatetime' | 'adjustamount'> | null {
  if (newStatus === 'OPEN' || newStatus === 'HOLD') {
    return { approvedby: '', approveddatetime: '', adjustamount: 0 };
  }
  return null;
}

/**
 * Number of line rows to render — 6 normally, but grows to N+2 when the item
 * already has more than 4 lines so all of them are editable
 * (V1 lib_editrecord.pl:5228 `$maxli`).
 */
export function lineRowCount(existingCount: number): number {
  return existingCount > 4 ? existingCount + 2 : 6;
}

const num = (v: number | null | undefined): number => Number(v || 0);

/**
 * Validate a cost item for save. Returns the first blocking error message, or
 * null when the item is valid. Mirrors V1's `$saveclick` JS plus the GWI-added
 * Department-on-PAY guard that we keep intentionally.
 */
export function validateCostItem(form: CostItem): string | null {
  const lines = form.lineItems.filter((li) => li.deleted !== 'Y');

  // Type is required on every save (kept; V1 only requires it for approval).
  if (!form.costtype) return 'Type is required';

  // Both Qty AND Rate, or neither (V1 lib_editrecord.pl:5588).
  for (let i = 0; i < form.lineItems.length; i++) {
    const li = form.lineItems[i];
    const hasQty = num(li.qty) > 0;
    const hasRate = num(li.rate) !== 0;
    if ((hasQty || hasRate) && !(hasQty && hasRate)) {
      return 'Line items must have BOTH Qty and Rate filled, or both empty';
    }
  }

  // Hold Type required when status is HOLD (V1 lib_editrecord.pl:5864).
  if (form.status === 'HOLD' && !form.holdtyperecno) {
    return 'Hold Type is required when status is HOLD';
  }

  // Department required on PAY lines (kept GWI guard).
  if (lines.some((li) => li.paystatus === 'PAY' && !(li.groupresponsible ?? '').trim())) {
    return 'Department is required for all PAY-status line items';
  }

  // NOTE: the header Reason is NOT required on PAY lines. V1 had that guard and
  // commented it out in Aug 2016 (#32705, lib_editrecord.pl:5817); we dropped it
  // 2026-09-08 — each line carries its own Pay Reason column.

  // Dispute Reason required when dispute is marked.
  if (form.dispute === 'Y' && !(form.disputereason ?? '').trim()) {
    return 'Dispute Reason is required when dispute is marked';
  }

  const approvedLike = form.status === 'APPROVED' || form.status === 'MANUAL TRANSFER';
  if (approvedLike) {
    // Required identity fields for approval (V1 lib_editrecord.pl:5833).
    if (!form.vendorcode) return 'Vendor is required for APPROVED/MANUAL TRANSFER status';
    if (!(form.invoicenumber ?? '').trim()) return 'Invoice Number is required for Approval/Manual Transfer.';
    if (!(form.invoicedate ?? '').trim()) return 'Invoice Date is required for Approval/Manual Transfer.';
    if (!(form.receiveddate ?? '').trim()) return 'Received Date is required for Approval/Manual Transfer.';

    // Cannot approve with a line in DISPUTE (V1 lib_editrecord.pl:5809).
    if (lines.some((li) => li.paystatus === 'DISPUTE')) {
      return 'Status cannot be APPROVED/MANUAL TRANSFER when a line Pay Status is in DISPUTE.';
    }

    // GL Code on non-WAIVED/NOT RECEIVED content lines (V1 lib_editrecord.pl:5813).
    const missingGL = lines.some((li) => {
      const hasContent = num(li.qty) > 0 || num(li.rate) !== 0;
      if (!hasContent) return false;
      if (!li.paystatus || ['WAIVED', 'NOT RECEIVED'].includes(li.paystatus)) return false;
      return !(li.glcode ?? '').trim();
    });
    if (missingGL) {
      return 'GL Code is required on all non-WAIVED/NOT RECEIVED line items for APPROVED/MANUAL TRANSFER status';
    }

    // Line total must be > 0 when the line has a Qty (V1 lib_editrecord.pl:5827).
    for (let i = 0; i < form.lineItems.length; i++) {
      const li = form.lineItems[i];
      if (li.deleted === 'Y') continue;
      if (num(li.qty) > 0 && num(li.total) <= 0) {
        return `Line Item Total must be greater than 0. Line #${i + 1}`;
      }
    }
  }

  // Invoice # and Invoice Date must pair (V1 lib_editrecord.pl:5799).
  const hasInvNum = (form.invoicenumber ?? '').trim();
  const hasInvDate = (form.invoicedate ?? '').trim();
  if ((hasInvNum && !hasInvDate) || (!hasInvNum && hasInvDate)) {
    return 'Invoice # and Invoice Date must both be filled or both left empty';
  }

  // PO# digits-only for FREIGHT BILL (V1 lib_editrecord.pl:5783).
  if (form.costtype === 'FREIGHT BILL' && form.ponumber && /\D/.test(form.ponumber)) {
    return 'PO# must contain only digits when Type is FREIGHT BILL';
  }

  return null;
}
